import {
  ChangeEvent,
  CSSProperties,
  Fragment,
  MouseEvent as ReactMouseEvent,
} from "react"

import Button from "ui/buttons/Button"
import { Checkbox } from "@material-ui/core"
import { DAYS_OF_WEEK } from "../../RateCardDetails/BaseRate/utils"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { Exclusion } from "models/packages"
import { HourRangePicker } from "components/HourPicker/HourRangePicker"
import IconButton from "ui/buttons/IconButton"
import Options from "components/inputs/Options"
import { css } from "@emotion/css"
import { useI18n } from "providers/I18n"

type Props = {
  exclusions: {
    exclusions?: Exclusion[] | undefined
    checked: boolean
  }
  onExclusionsChange: (
    name?: string | undefined,
    value?: string | undefined,
  ) => void
  onExclusionsRemove: (
    e: ReactMouseEvent<HTMLButtonElement>,
    idx: number,
  ) => void
  onExclusionsCheck?: (checked: boolean) => void
  selectedDays?: string[]
  hideCheckbox?: boolean
  allowed?: boolean
}

export function Exclusions(props: Props) {
  const { t } = useI18n()
  const {
    exclusions: propExclusions,
    onExclusionsChange,
    onExclusionsRemove,
    onExclusionsCheck,
    hideCheckbox,
    selectedDays,
    allowed = true,
  } = props
  const exclusions = !!propExclusions.exclusions?.length
    ? propExclusions.exclusions
    : [{}]

  const handleChange = (
    evt?: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { value, name } = evt?.target ?? {}
    onExclusionsChange(name, value)
  }

  const onTimeBeltChange =
    (idx: number) =>
    ({ name, value }: { name: string; value?: string }) => {
      onExclusionsChange(`${idx}.${name}`, value)
    }

  const onLineAdd = () => {
    onExclusionsChange()
  }

  const onCheckBoxChange = (
    _: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onExclusionsCheck && onExclusionsCheck(checked)
  }

  const daysOfWeek = DAYS_OF_WEEK.map((d) => ({
    value: d,
    label: t(`days.${d}`),
  }))

  const selectedExclusionDays = daysOfWeek?.filter((d) =>
    selectedDays?.includes(d?.value),
  )

  const style = {
    "--grid-first-column": !hideCheckbox ? "115px" : "0",
  } as CSSProperties

  return (
    <div style={style} className={styles.beltSection}>
      {!hideCheckbox ? (
        <div className={styles.excludeContainer}>
          <Checkbox
            className={styles.excludeCheckbox}
            size="medium"
            color="primary"
            checked={propExclusions?.checked}
            onChange={onCheckBoxChange}
            disabled={!allowed}
          />
          <div style={{ alignSelf: "center", marginRight: 42 }}>
            {t("packages.broadcastHours.exclude")}
          </div>
        </div>
      ) : null}
      {exclusions?.map((e, idx) => {
        return (
          <Fragment key={idx}>
            <div className={styles.options}>
              <Options
                name={`${idx}.day`}
                options={selectedExclusionDays}
                value={e?.day}
                size="medium"
                className={styles.select}
                onChange={handleChange}
                disabled={!propExclusions?.checked || !allowed}
                label={t("exclusions.select.date")}
              />
              <HourRangePicker
                start={e?.start || ""}
                end={e?.end || ""}
                onChange={onTimeBeltChange(idx)}
                disabled={!propExclusions?.checked || !allowed}
              />
            </div>
            {allowed ? (
              <IconButton
                className={styles.iconButton}
                onClick={(e: ReactMouseEvent<HTMLButtonElement>) =>
                  onExclusionsRemove(e, idx)
                }
              >
                <DeleteIcon color="inherit" className={styles.deleteIcon} />
              </IconButton>
            ) : null}
          </Fragment>
        )
      })}
      {allowed ? (
        <Button
          className={styles.textButton}
          color="primary"
          onClick={onLineAdd}
          disabled={!propExclusions?.checked}
        >
          {t("addMore")}
        </Button>
      ) : null}
    </div>
  )
}

const styles = {
  beltSection: css`
    font-size: 14px;
    color: var(--black);
    display: grid;
    grid-template-columns: var(--grid-first-column) repeat(2, min-content);
    grid-row-gap: var(--spacing-m);
    align-items: center;
  `,
  excludeContainer: css`
    display: flex;
  `,
  excludeCheckbox: css`
    padding: 0px;
    margin-left: -4px;
    margin-right: 6px;
  `,
  options: css`
    display: flex;
    padding: var(--spacing-xs);
    background-color: var(--lighter-grey);
    grid-column-start: 2;
    grid-column-end: 3;
    align-items: center;
  `,
  select: css`
    width: 134px;
    margin-right: 16px;
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 1;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
    &[aria-disabled="true"] {
      background-color: transparent;
    }
    grid-column-start: 2;
    grid-column-end: 3;
    padding-left: var(--spacing-xs);
  `,
  iconButton: css`
    margin-left: var(--spacing-s);
    &.MuiIconButton-root {
      margin-left: var(--spacing-s);
      margin-top: var(--spacing-xs);
    }
  `,
  deleteIcon: css`
    width: 24px;
    height: 24px;
    cursor: pointer;
    > path {
      fill: var(--grey);
    }
  `,
}
